<template>
  <div class="log-con">
    <LoginHeader />
    <div class="social-login-wrap">
      <button v-if="isIOS" type="button" class="social-btn l-btn apple" @click="onClickLoginApple">
        <img src="/images/mobile/icon/apple-logo.svg" alt="" />
        <span>{{ t('continue_with_social', { social: 'Apple' }) }}</span>
      </button>
      <button type="button" class="social-btn l-btn facebook" @click="onClickLoginFacebook">
        <img src="/images/mobile/icon/facebook-logo.svg" alt="" />
        <span>{{ t('continue_with_social', { social: 'Facebook' }) }}</span>
      </button>
      <button type="button" class="social-btn l-btn google" @click="onClickLoginGoogle">
        <img src="/images/mobile/icon/google-logo.svg" alt="" />
        <span>{{ t('continue_with_social', { social: 'Google' }) }}</span>
      </button>
    </div>
    <div class="or">
      <span>{{ t('or') }}</span>
    </div>

    <RouterLink to="/app/adduse" class="gray-btn l-btn" aria-label="">
      <span>{{ t('ss_login_as_sub') }}</span>
    </RouterLink>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import type { AxiosError } from 'axios';

import { loginSocialMobile } from '@/api/apiService';
import api from '@/api/index';
import LoginHeader from '@/components/domains/mobile/log/LoginHeader.vue';
import { useUserStore } from '@/stores/user';
import { logoutSocialMobile } from '@/utils/authService';
import { secureStorageSet, navigateInApp } from '@/utils/native/nativeBridge';

const { t } = useI18n();
const userStore = useUserStore();
// const router = useRouter();

const isLoggingIn = ref(false);

const isIOS = Capacitor.getPlatform() === 'ios';
// const isLocalTest = true;
const baseURL = api.defaults.baseURL ?? '';

const onClickLoginGoogle = async () => {
  if (isLoggingIn.value) return;

  try {
    await logoutSocialMobile('google');
  } catch (error) {
    console.debug(error);
  }

  try {
    isLoggingIn.value = true;

    const resLoginSocial = await SocialLogin.login({
      provider: 'google',
      options: { prompt: 'select_account' },
    });

    if (resLoginSocial.result.responseType !== 'online') {
      throw new Error('Google login is offline mode');
    }

    const { idToken, profile } = resLoginSocial.result;

    if (!idToken || !profile || !profile.email) {
      throw new Error('Missing accessToken or profile from Google login');
    }

    await loginToSconChat({
      provider: 'THIRD_PARTY_GOOGLE',
      credential: idToken,
      id: profile.email,
      email: profile.email ?? '',
    });
  } catch (error) {
    console.error('GOOGLE ERROR', error);
  } finally {
    isLoggingIn.value = false;
  }
};

const onClickLoginFacebook = async () => {
  if (isLoggingIn.value) return;

  await logoutSocialMobile('facebook');

  try {
    isLoggingIn.value = true;

    const resLoginSocial = await SocialLogin.login({
      provider: 'facebook',
      options: {
        permissions: ['email', 'public_profile'],
        limitedLogin: false,
      },
    });

    const { accessToken, profile } = resLoginSocial.result;

    if (!accessToken || !profile || !profile.userID) {
      throw new Error('Missing accessToken or profile from Facebook login');
    }
    await loginToSconChat({
      provider: 'THIRD_PARTY_META',
      credential: accessToken.token,
      id: profile.userID,
      email: profile.email ?? '',
    });
  } catch (error) {
    console.error('FB ERROR', error);
  } finally {
    isLoggingIn.value = false;
  }
};

const onClickLoginApple = async () => {
  if (isLoggingIn.value) return;

  await logoutSocialMobile('apple');

  try {
    isLoggingIn.value = true;

    const resLoginSocial = await SocialLogin.login({
      provider: 'apple',
      options: {},
    });
    console.log('APPLE', resLoginSocial);

    const { idToken, profile } = resLoginSocial.result;

    if (!idToken || !profile || !profile.email) {
      throw new Error('Missing accessToken or profile from Apple login');
    }
    await loginToSconChat({
      provider: 'THIRD_PARTY_APPLE',
      credential: idToken,
      id: profile.user,
      email: profile.email ?? '',
      name: profile.givenName ?? '', // TODO 애플은 이름을 매번 주는게 아니라서, 회원가입할 때 이름 입력 받는 로직 필요할듯
    });
  } catch (error) {
    console.error('APPLE ERROR', error);
  } finally {
    isLoggingIn.value = false;
  }
};

interface PayloadLogin {
  provider: 'THIRD_PARTY_GOOGLE' | 'THIRD_PARTY_META' | 'THIRD_PARTY_APPLE';
  credential: string;
  id: string;
  email: string;
  name?: string;
}

const loginToSconChat = async (payloadLogin: PayloadLogin) => {
  try {
    await secureStorageSet({ key: 'authToken', value: payloadLogin.credential });

    const resLoginScon = await loginSocialMobile(payloadLogin);
    if (!resLoginScon.success) return;

    const { accessToken, refreshToken } = resLoginScon.data;

    await userStore.setLoginInfo({
      token: accessToken,
      loginType: 'SELF_SERVICE',
      partnerCode: null,
      refreshToken,
    });
    await userStore.fetchUserInfo();

    await secureStorageSet({
      key: 'authSession',
      value: JSON.stringify({ refreshToken, provider: payloadLogin.provider }),
    });

    goToDashboard();
  } catch (error: unknown) {
    const err = error as AxiosError<{ code?: number; errorCode?: number; message?: string }>;
    const errorCode = err.errorCode ?? err.response?.data?.code ?? err.response?.data?.errorCode;

    console.error('SCON ERROR', error, `ERROR CODE: ${errorCode}`);

    // TODO 502 에러 등 네트워크 이슈 모달
    if (errorCode === 1004) {
      goToSignup(payloadLogin);
      return;
    } else {
      alert(t('error_server_or_network'));
    }
  }
};

const goToDashboard = () => {
  // if (isLocalTest) {
  //   console.debug('888 is local test');
  //   router.push({ name: 'app-main-view' });
  //   return;
  // }
  navigateInApp(`${baseURL}/app/main`);
};

const goToSignup = async (payloadLogin: PayloadLogin) => {
  const { id: userId, provider, credential: authToken, email } = payloadLogin;
  const oAuth = provider.split('_').at(-1)?.toLowerCase();

  await secureStorageSet({
    key: 'signupInfo',
    value: JSON.stringify({
      oAuth,
      userId,
      accessToken: authToken,
      email,
      ...(oAuth === 'apple' && { nickname: payloadLogin.name }),
    }),
  });

  // if (isLocalTest) {
  //   const router = useRouter();
  //   router.push({ name: 'app-signup' });
  //   return;
  // }
  navigateInApp(`${baseURL}/app/signup`);
};

const initSocialLogin = async () => {
  try {
    await SocialLogin.initialize({
      google: {
        iOSClientId: import.meta.env.VITE_CLIENT_ID_GOOGLE_IOS,
        webClientId: import.meta.env.VITE_CLIENT_ID_GOOGLE_WEB,
        mode: 'online',
      },
      facebook: {
        appId: import.meta.env.VITE_APP_ID_FACEBOOK,
        clientToken: import.meta.env.VITE_CLIENT_ID_FACEBOOK,
      },
      ...(isIOS ? { apple: {} } : {}),
    });
  } catch (error) {
    console.error('SOCIAL LOGIN INIT ERROR', error);
  }
};

onMounted(async () => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  await initSocialLogin();
});
</script>
<style scoped lang="scss">
.log-con {
  padding: $app-padding;
  .social-login-wrap {
    .social-btn {
      width: 100%;
      border-radius: $size-s;
      margin-bottom: $size-s;
      &:last-child {
        margin-bottom: 0;
      }
      &.apple {
        background: #000;
        span {
          color: #fff;
        }
      }
      &.facebook {
        background: #0866ff;
        span {
          color: #fff;
        }
      }
      &.google {
        background: #fff;
        border: 1px solid $gray50;
        span {
          color: $color-gray-900;
        }
      }
    }
  }
  .or {
    position: relative;
    text-align: center;
    &::before {
      content: '';
      position: absolute;
      display: block;
      width: 100%;
      height: 1px;
      background: $color-gray-30;
      top: 50%;
      transform: translateY(-50%);
    }
    span {
      position: relative;
      background: #fff;
      z-index: 1;
      display: inline-block;
      padding: 1rem 0.5rem;
      color: $color-gray-80;
      font-size: $font-size-xxs;
    }
  }
  .gray-btn {
    text-decoration: none;
    span {
      color: $color-gray-900;
    }
  }
}
</style>
